import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EntityRef, EntityType } from 'src/app/core/settings/settings.service';
import {
  Entity,
  EntitySearchService,
} from 'src/app/shared/entity-search/entity-search.service';
import {
  ActionModalComponent,
  ActionModalConfig,
  ActionModalResult,
} from '../../shared/action-modal/action-modal.component';
import {
  CohortApprovalModalComponent,
  CohortApprovalResult,
} from '../cohort-approval-modal/cohort-approval-modal.component';
import {
  CreateCohortComponent,
  CreateCohortResult,
} from '../create-cohort/create-cohort.component';
import {
  Cohort,
  CohortEntity,
  CohortService,
} from '../services/cohort.service';

@Component({
  selector: 'dcc-cohort-modal',
  templateUrl: './cohort-modal.component.html',
  styleUrls: ['./cohort-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class CohortModalComponent implements OnInit {
  cohort: Cohort;
  legacyUnknownEntities: CohortEntity[] = []; //Only exists because of old cohorts where we did not do any sort of entity validation.
  entities: Entity[] = [];

  constructor(
    private activeModal: NgbActiveModal,
    private cohortService: CohortService,
    private entityService: EntitySearchService,
    private modalService: NgbModal,
  ) {}

  ngOnInit(): void {
    this.getEntityData();
  }

  async editCohort() {
    const modal = this.modalService.open(CreateCohortComponent, {
      size: 'lg',
      windowClass: 'lytxModal',
      centered: true,
    });

    modal.componentInstance.cohort = this.cohort;

    modal.closed.subscribe(async (result: CreateCohortResult) => {
      if (result.success) {
        this.close();
      }
    });
  }

  async reviewChanges() {
    const modal = this.modalService.open(CohortApprovalModalComponent, {
      windowClass: 'custom-modal',
      centered: true,
    });

    modal.componentInstance.cohort = this.cohort;

    modal.closed.subscribe(async (result: CohortApprovalResult) => {
      if (result.success) {
        this.close();
      }
    });
  }

  async deleteCohort() {
    const modal = this.modalService.open(ActionModalComponent, {
      windowClass: 'deleteCohortModal',
      centered: true,
    });

    modal.componentInstance.config = <ActionModalConfig>{
      title: 'Delete Cohort ' + this.cohort.name,
      body: 'Are you sure you want to delete this cohort? This will remove the cohort from any upcoming releases.',
      action: 'Delete',
    };

    modal.closed.subscribe(async (result: ActionModalResult) => {
      if (result.success) {
        await this.cohortService.deleteCohort(this.cohort.name);
        this.close();
      }
    });
  }

  close() {
    this.activeModal.close();
  }

  async toggleEnabledStatus() {
    if (this.cohort.awaitingCreationApproval) {
      return;
    }

    if (this.cohort.disabledDate) {
      await this.saveCohortStatus(true);
      this.close();
    } else {
      this.disableCohort();
    }
  }

  private disableCohort() {
    const modal = this.modalService.open(ActionModalComponent, {
      windowClass: 'disableCohortModal',
      centered: true,
    });

    modal.componentInstance.config = <ActionModalConfig>{
      title: 'Disable Cohort ' + this.cohort.name,
      body: 'Are you sure you want to disable this cohort? This will remove the cohort from any upcoming releases for which the cohort is scheduled.',
      action: 'Disable',
    };

    modal.closed.subscribe(async (result: ActionModalResult) => {
      if (result.success) {
        await this.saveCohortStatus(false);
        this.close();
      }
    });
  }

  private async saveCohortStatus(enable = false): Promise<void> {
    await this.cohortService.saveCohortStatus(this.cohort.name, enable);
  }

  private async getEntityData() {
    this.entities = [];
    this.legacyUnknownEntities = [];

    this.cohort?.entities.forEach(async (entity) => {
      const resp =
        entity.id === '00000000-0000-0000-0000-000000000000'
          ? [fullFleetGroupEntity]
          : await this.entityService.searchEntities(entity.id);
      if (resp.length > 0) {
        this.entities.push(resp[0]);
      } else {
        this.legacyUnknownEntities.push(entity);
      }
    });
  }
}

const fullFleetGroupEntity: Entity = {
  id: '00000000-0000-0000-0000-000000000000',
  name: 'Full Fleet',
  path: [
    {
      name: 'Full Fleet',
      id: '00000000-0000-0000-0000-000000000000',
      entityType: EntityType.group,
    } as EntityRef,
  ],
  rootGroupId: '00000000-0000-0000-0000-000000000000',
  entityType: EntityType.group,
};
